import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pill } from '@/components/widgets/Pill';
import { adminDeals } from '@/api/admin';
import { fmtDateTime, fmtMoney, fmtNum, moneyToNumber } from '@/lib/format';
import type { Deal, DealReconcile, DealType, FollowMode, Money, NearbyUserSettlement, RealDealAllocation } from '@/api/types';

const DEAL_TYPE_LABEL: Record<DealType, string> = {
  OPEN: '开仓',
  CLOSE: '平仓',
  PARTIAL_CLOSE: '部分平仓',
};
const DEAL_TYPE_TONE: Record<DealType, 'up' | 'warn' | 'neutral'> = {
  OPEN: 'neutral',
  CLOSE: 'up',
  PARTIAL_CLOSE: 'warn',
};
const FOLLOW_MODE_LABEL: Record<FollowMode, string> = { REVERSE: '反向', FOLLOW: '跟随' };
const FOLLOW_MODE_TONE: Record<FollowMode, 'brand' | 'up'> = { REVERSE: 'brand', FOLLOW: 'up' };
const ALLOCATION_ROLE_LABEL = {
  EXPOSURE_OWNER: '敞口归属',
  BASIS: '基差',
  TRIGGER: '触发者',
} as const;
const STEP_TYPE_LABEL: Record<string, string> = {
  OPEN: '开净仓',
  ADD: '加净仓',
  REDUCE: '减净仓',
  CLOSE: '平净仓',
  FLIP_CLOSE: '反手先平',
  FLIP_OPEN: '反手再开',
  LEDGER_ONLY: '账本',
};

const lots = (v: number) => (v / 100).toFixed(2);
const price = (v: number | null | undefined) => (v == null ? '—' : fmtNum(v, 2));

type DetailTab = 'deal' | 'position' | 'allocations' | 'users';

interface Props {
  /** 非空 = 打开弹窗展示该成交;null = 关闭。 */
  deal: Deal | null;
  symbolName: string;
  onClose: () => void;
}

/**
 * 「真实成交明细」成交详情弹窗(管理端,§9.8)。从真实账户成交台账某行下钻打开。
 *
 * 页面分三层口径:本笔 cTrader deal、同真实仓累计、用户虚拟账本桥接。用户桥接只解释 Netting
 * 账本关系,不声明逐笔真实成交强归因。
 */
export function DealDetailDialog({ deal, symbolName, onClose }: Props) {
  const [tab, setTab] = useState<DetailTab>('deal');
  const reconcileQ = useQuery({
    queryKey: ['admin', 'deal-reconcile', deal?.id],
    queryFn: () => adminDeals.reconcile(deal!.id),
    enabled: !!deal?.id,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (deal) setTab('deal');
  }, [deal]);

  const detailDeal = reconcileQ.data?.deal ?? deal;

  return (
    <Dialog open={!!deal} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
        {detailDeal && (
          <div className="space-y-4">
            <DialogHeader>
              <DialogTitle className="flex flex-wrap items-center gap-2">
                成交详情
                {detailDeal.followMode && (
                  <Pill tone={FOLLOW_MODE_TONE[detailDeal.followMode]}>{FOLLOW_MODE_LABEL[detailDeal.followMode]}</Pill>
                )}
                <Pill tone={detailDeal.side === 'BUY' ? 'up' : 'down'}>{detailDeal.side}</Pill>
                <Pill tone={DEAL_TYPE_TONE[detailDeal.dealType]}>{DEAL_TYPE_LABEL[detailDeal.dealType]}</Pill>
                {detailDeal.detached && (
                  <Pill tone="warn" title="该虚拟腿成交时已脱钩:背后没有真实对冲腿,盈亏由平台承担">
                    已脱钩
                  </Pill>
                )}
              </DialogTitle>
              <DialogDescription className="num">
                {fmtDateTime(detailDeal.executedAt)} · {symbolName} · 成交 #{detailDeal.id}
              </DialogDescription>
            </DialogHeader>

            <div className="flex gap-1 rounded-md bg-bg-3 p-1">
              <TabButton active={tab === 'deal'} onClick={() => setTab('deal')}>本笔成交</TabButton>
              <TabButton active={tab === 'position'} onClick={() => setTab('position')}>同仓对账</TabButton>
              <TabButton active={tab === 'allocations'} onClick={() => setTab('allocations')}>精确归因</TabButton>
              <TabButton active={tab === 'users'} onClick={() => setTab('users')}>用户桥接</TabButton>
            </div>

            {tab === 'deal' && <DealTab deal={detailDeal} />}
            {tab === 'position' && (
              <PositionTab
                reconcile={reconcileQ.data}
                loading={reconcileQ.isLoading}
                error={reconcileQ.error}
              />
            )}
            {tab === 'allocations' && (
              <AllocationTab
                reconcile={reconcileQ.data}
                loading={reconcileQ.isLoading}
                error={reconcileQ.error}
              />
            )}
            {tab === 'users' && (
              <UserBridgeTab
                reconcile={reconcileQ.data}
                loading={reconcileQ.isLoading}
                error={reconcileQ.error}
              />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DealTab({ deal }: { deal: Deal }) {
  return (
    <div className="space-y-4">
      <Section title="对账锚点(cTrader 真实账户)">
        <KV k="真实订单号"><CopyText value={deal.orderId} /></KV>
        <KV k="真实仓 ID">
          {deal.positionId
            ? <CopyText value={deal.positionId} />
            : <span className="text-ink-3">净仓 / 未关联</span>}
        </KV>
        <KV k="成交 ID"><span className="num text-ink-2">{deal.id}</span></KV>
        <KV k="触发用户">
          {deal.userId ? (
            <Link
              to={`/user-orders?userId=${deal.userId}`}
              className="num text-brand hover:underline"
              title="在『用户订单』页查看该用户"
            >
              {deal.userId}
            </Link>
          ) : (
            <span className="text-ink-3">—</span>
          )}
        </KV>
      </Section>

      <ScalingSection deal={deal} />

      <Section title="金额明细">
        <KV k="成交价"><span className="num text-ink-1">{fmtNum(deal.price, 2)}</span></KV>
        <KV k="盈亏(本笔)"><MoneyText m={deal.pnl} /></KV>
        <KV k="佣金(本笔)"><MoneyText m={deal.commission} /></KV>
        <KV k="库存费"><MoneyText m={deal.swap} /></KV>
        <KV k="净盈亏(本笔)"><MoneyText m={deal.netPnl} emphasized /></KV>
      </Section>
    </div>
  );
}

function PositionTab({ reconcile, loading, error }: {
  reconcile?: DealReconcile;
  loading: boolean;
  error: unknown;
}) {
  if (loading) return <Panel>正在加载同仓对账...</Panel>;
  if (error) return <Panel tone="down">{errorMessage(error)}</Panel>;
  if (!reconcile) return <Panel>暂无对账数据</Panel>;
  const p = reconcile.realPosition;

  return (
    <div className="space-y-4">
      <Section title="真实仓累计">
        <KV k="真实仓 ID">{p.positionId ? <CopyText value={p.positionId} /> : <span className="text-ink-3">—</span>}</KV>
        <KV k="状态"><span className="text-ink-1">{p.status ?? '—'}</span></KV>
        <KV k="开仓时间"><span className="num text-ink-1">{p.openedAt ? fmtDateTime(p.openedAt) : '—'}</span></KV>
        <KV k="平仓时间"><span className="num text-ink-1">{p.closedAt ? fmtDateTime(p.closedAt) : '—'}</span></KV>
        <KV k="同仓成交合计"><MoneyText m={p.sumDealNetPnl} emphasized /></KV>
        <KV k="仓位累计净额"><MoneyText m={p.closedPnl} emphasized /></KV>
        <KV k="差异"><MoneyText m={p.diff} emphasized /></KV>
      </Section>

      <div>
        <div className="mb-1.5 text-xs text-ink-2">同仓真实成交</div>
        <div className="overflow-x-auto rounded-md border border-line bg-bg-3">
          <table className="min-w-full text-xs">
            <thead className="text-ink-3">
              <tr>
                <th className="px-3 py-2 text-left font-normal">时间</th>
                <th className="px-3 py-2 text-left font-normal">订单号</th>
                <th className="px-3 py-2 text-center font-normal">开平</th>
                <th className="px-3 py-2 text-center font-normal">方向</th>
                <th className="px-3 py-2 text-right font-normal">手数</th>
                <th className="px-3 py-2 text-right font-normal">成交价</th>
                <th className="px-3 py-2 text-right font-normal">本笔净盈亏</th>
              </tr>
            </thead>
            <tbody>
              {reconcile.positionDeals.map((d) => (
                <tr key={d.id} className="border-t border-line/60">
                  <td className="px-3 py-2 num text-ink-2">{fmtDateTime(d.executedAt)}</td>
                  <td className="px-3 py-2 num text-ink-1">{d.orderId}</td>
                  <td className="px-3 py-2 text-center">
                    <Pill tone={DEAL_TYPE_TONE[d.dealType]}>{DEAL_TYPE_LABEL[d.dealType]}</Pill>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <Pill tone={d.side === 'BUY' ? 'up' : 'down'}>{d.side}</Pill>
                  </td>
                  <td className="px-3 py-2 num text-right text-ink-1">{lots(d.volume)}</td>
                  <td className="px-3 py-2 num text-right text-ink-1">{fmtNum(d.price, 2)}</td>
                  <td className="px-3 py-2 num text-right"><MoneyText m={d.netPnl} emphasized /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AllocationTab({ reconcile, loading, error }: {
  reconcile?: DealReconcile;
  loading: boolean;
  error: unknown;
}) {
  if (loading) return <Panel>正在加载精确归因...</Panel>;
  if (error) return <Panel tone="down">{errorMessage(error)}</Panel>;
  if (!reconcile) return <Panel>暂无归因数据</Panel>;

  return (
    <div className="space-y-4">
      <Section title="本笔成交分摊校验">
        <KV k="本笔真实净额"><MoneyText m={reconcile.allocationBridge.actualDealNet} emphasized /></KV>
        <KV k="归因合计"><MoneyText m={reconcile.allocationBridge.allocatedNet} emphasized /></KV>
        <KV k="未闭合差额"><MoneyText m={reconcile.allocationBridge.basis} emphasized /></KV>
        <KV k="归因状态">
          <Pill tone={reconcile.allocationBridge.hasExactAllocation ? 'up' : 'warn'}>
            {reconcile.allocationBridge.hasExactAllocation ? '已匹配快照' : '无快照'}
          </Pill>
        </KV>
        <div className="col-span-2 text-xs text-ink-3">{reconcile.allocationBridge.note}</div>
      </Section>

      {reconcile.realDealAllocations.length === 0 ? (
        <Panel>该真实成交没有二期精确归因记录,通常是历史成交或真实成交事件未能匹配诊断 step。</Panel>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-bg-3">
          <table className="min-w-full text-xs">
            <thead className="text-ink-3">
              <tr>
                <th className="px-3 py-2 text-left font-normal">归属</th>
                <th className="px-3 py-2 text-left font-normal">用户订单</th>
                <th className="px-3 py-2 text-center font-normal">净仓动作</th>
                <th className="px-3 py-2 text-right font-normal">目标变化</th>
                <th className="px-3 py-2 text-right font-normal" title="净仓 step 创建时间到真实成交回执关联时间">真实耗时</th>
                <th className="px-3 py-2 text-right font-normal">归因手数</th>
                <th className="px-3 py-2 text-right font-normal">盈亏+库存</th>
                <th className="px-3 py-2 text-right font-normal">佣金</th>
                <th className="px-3 py-2 text-right font-normal">合计</th>
              </tr>
            </thead>
            <tbody>
              {reconcile.realDealAllocations.map((a) => <AllocationRow key={a.allocationId} allocation={a} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AllocationRow({ allocation }: { allocation: RealDealAllocation }) {
  const stepLabel = allocation.stepType ? (STEP_TYPE_LABEL[allocation.stepType] ?? allocation.stepType) : '—';
  const targetShift = allocation.targetBeforeLots100 != null && allocation.targetAfterLots100 != null
    ? `${lots(allocation.targetBeforeLots100)} → ${lots(allocation.targetAfterLots100)}`
    : '—';
  const latencyTitle = allocation.stepCreatedAt && allocation.stepLinkedAt
    ? `${fmtDateTime(allocation.stepCreatedAt)} → ${fmtDateTime(allocation.stepLinkedAt)}`
    : undefined;

  return (
    <tr className="border-t border-line/60">
      <td className="px-3 py-2">
        <Pill tone={allocationRoleTone(allocation.role)}>
          {ALLOCATION_ROLE_LABEL[allocation.role] ?? allocation.role}
        </Pill>
        <div className="mt-1 text-[10px] text-ink-3">{allocation.method}</div>
      </td>
      <td className="px-3 py-2">
        {allocation.userId ? (
          <Link to={`/user-orders?userId=${allocation.userId}`} className="num text-brand hover:underline">
            {allocation.phone ?? allocation.userId}
          </Link>
        ) : (
          <span className="text-ink-3">—</span>
        )}
        <div className="num text-[10px] text-ink-3">
          {allocation.userOrderId ? `order ${allocation.userOrderId}` : 'order —'} · {allocation.legId ? `leg ${allocation.legId}` : 'leg —'}
        </div>
      </td>
      <td className="px-3 py-2 text-center">
        <Pill tone="neutral">{stepLabel}</Pill>
        {allocation.stepNo != null && <div className="num mt-1 text-[10px] text-ink-3">step {allocation.stepNo}</div>}
      </td>
      <td className="px-3 py-2 num text-right text-ink-1">{targetShift}</td>
      <td className="px-3 py-2 num text-right text-ink-1" title={latencyTitle}>{formatLatency(allocation.stepLatencyMs)}</td>
      <td className="px-3 py-2 num text-right text-ink-1">{lots(allocation.allocatedRealVolume)}</td>
      <td className="px-3 py-2 num text-right"><MoneyText m={allocation.allocatedPnl} /></td>
      <td className="px-3 py-2 num text-right"><MoneyText m={allocation.allocatedCommission} /></td>
      <td className="px-3 py-2 num text-right"><MoneyText m={allocation.allocatedNet} emphasized /></td>
    </tr>
  );
}

function formatLatency(ms: number | null | undefined) {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`;
  const seconds = ms / 1000;
  return `${seconds < 10 ? seconds.toFixed(2) : seconds.toFixed(1)} s`;
}

function UserBridgeTab({ reconcile, loading, error }: {
  reconcile?: DealReconcile;
  loading: boolean;
  error: unknown;
}) {
  if (loading) return <Panel>正在加载用户桥接...</Panel>;
  if (error) return <Panel tone="down">{errorMessage(error)}</Panel>;
  if (!reconcile) return <Panel>暂无桥接数据</Panel>;

  return (
    <div className="space-y-4">
      <div className="space-y-1 rounded-md border border-warn/30 bg-warn/5 p-3 text-xs text-ink-2">
        {reconcile.warnings.map((w) => <div key={w}>{w}</div>)}
      </div>

      <Section title="桥接汇总">
        <KV k="真实同仓净额"><MoneyText m={reconcile.basisBridge.actualRealNet} emphasized /></KV>
        <KV k="用户预期贡献"><MoneyText m={reconcile.basisBridge.expectedRealContribution} emphasized /></KV>
        <KV k="基差"><MoneyText m={reconcile.basisBridge.basis} emphasized /></KV>
        <div className="col-span-2 text-xs text-ink-3">{reconcile.basisBridge.note}</div>
      </Section>

      <div>
        <div className="mb-1.5 text-xs text-ink-2">附近用户结算</div>
        {reconcile.nearbyUserSettlements.length === 0 ? (
          <Panel>该真实成交时间窗口内暂无用户结算</Panel>
        ) : (
          <div className="overflow-x-auto rounded-md border border-line bg-bg-3">
            <table className="min-w-full text-xs">
              <thead className="text-ink-3">
                <tr>
                  <th className="px-3 py-2 text-left font-normal">时间</th>
                  <th className="px-3 py-2 text-left font-normal">用户</th>
                  <th className="px-3 py-2 text-center font-normal">方向</th>
                  <th className="px-3 py-2 text-right font-normal">手数</th>
                  <th className="px-3 py-2 text-right font-normal">入场/平仓</th>
                  <th className="px-3 py-2 text-right font-normal">毛盈亏</th>
                  <th className="px-3 py-2 text-right font-normal">服务费</th>
                  <th className="px-3 py-2 text-right font-normal">净入账</th>
                  <th className="px-3 py-2 text-right font-normal">真实尺度贡献</th>
                </tr>
              </thead>
              <tbody>
                {reconcile.nearbyUserSettlements.map((s) => <SettlementRow key={`${s.legId}-${s.executedAt}`} s={s} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SettlementRow({ s }: { s: NearbyUserSettlement }) {
  return (
    <tr className="border-t border-line/60">
      <td className="px-3 py-2 num text-ink-2" title={s.valuationQuoteAt ? `价基时间 ${fmtDateTime(s.valuationQuoteAt)}` : undefined}>
        {fmtDateTime(s.executedAt)}
      </td>
      <td className="px-3 py-2">
        <Link to={`/user-orders?userId=${s.userId}`} className="num text-brand hover:underline">
          {s.phone ?? s.userId}
        </Link>
        <div className="num text-[10px] text-ink-3">leg {s.legId}</div>
      </td>
      <td className="px-3 py-2 text-center">
        <Pill tone={s.side === 'BUY' ? 'up' : 'down'}>{s.side}</Pill>
      </td>
      <td className="px-3 py-2 num text-right text-ink-1">{lots(s.volume)}</td>
      <td className="px-3 py-2 num text-right text-ink-1">{price(s.entryMark)} / {price(s.closeMark)}</td>
      <td className="px-3 py-2 num text-right"><MoneyText m={s.grossPnl} /></td>
      <td className="px-3 py-2 num text-right"><MoneyText m={s.serviceFee} /></td>
      <td className="px-3 py-2 num text-right"><MoneyText m={s.netToBalance} emphasized /></td>
      <td className="px-3 py-2 num text-right"><MoneyText m={s.expectedRealContribution} emphasized /></td>
    </tr>
  );
}

/** 缩放链路:用户虚拟方向/手数 × 比例 → 真实方向/手数 + 一致性推算。字段缺失时整段降级。 */
function ScalingSection({ deal }: { deal: Deal }) {
  const hasScaling =
    deal.virtualVolume != null
    && deal.virtualVolume > 0
    && deal.followRatioUsed != null
    && deal.followRatioUsed > 0;
  if (!hasScaling) {
    return (
      <Section title="缩放链路">
        <div className="col-span-2 text-sm text-ink-3">
          真实手数 {lots(deal.volume)} 手 · 缺触发用户手数(历史成交 / 未匹配诊断快照)
        </div>
      </Section>
    );
  }
  const virtualVolume = deal.virtualVolume as number;
  const ratio = deal.followRatioUsed as number;
  const virtualSide = deal.followMode === 'REVERSE'
    ? (deal.side === 'BUY' ? 'SELL' : 'BUY')
    : deal.side;
  const expected = Math.round(virtualVolume * ratio);
  const rawProductLots = (virtualVolume / 100) * ratio;
  const matches = expected === deal.volume;
  return (
    <Section title="缩放链路(R = round(用户手数 × 比例))">
      <KV k="用户虚拟方向">
        <span className="flex items-center gap-1.5">
          <Pill tone={virtualSide === 'BUY' ? 'up' : 'down'}>{virtualSide}</Pill>
          <span className="text-[10px] text-ink-3">推导</span>
        </span>
      </KV>
      <KV k="用户手数"><span className="num text-ink-1">{lots(virtualVolume)} 手</span></KV>
      <KV k="缩放比例"><span className="num text-ink-1">×{fmtNum(ratio, 2)}</span></KV>
      <KV k="真实手数"><span className="num text-ink-1">{lots(deal.volume)} 手</span></KV>
      <div className="col-span-2 flex items-center justify-between text-sm">
        <span className="text-ink-3">推算校验</span>
        <span className="flex items-center gap-1.5">
          <span
            className="num text-ink-2"
            title={`用户手数 × 比例 = ${rawProductLots.toFixed(4)} 手,按 0.01 手取整 = ${lots(expected)} 手`}
          >
            {lots(virtualVolume)} × {fmtNum(ratio, 2)} → {lots(expected)} 手
          </span>
          {matches
            ? <span className="text-[11px] text-up">一致</span>
            : <span className="text-[11px] text-warn" title="可能为最小手数 / 步进调整">与真实手数有差</span>}
        </span>
      </div>
    </Section>
  );
}

function allocationRoleTone(role: RealDealAllocation['role']): 'up' | 'warn' | 'brand' {
  if (role === 'BASIS') return 'warn';
  if (role === 'TRIGGER') return 'brand';
  return 'up';
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-3 py-1.5 text-sm transition ${active ? 'bg-bg-1 text-ink-1 shadow-sm' : 'text-ink-3 hover:text-ink-1'}`}
    >
      {children}
    </button>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-xs text-ink-2">{title}</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 rounded-md bg-bg-3 p-3">{children}</div>
    </div>
  );
}

function Panel({ children, tone }: { children: ReactNode; tone?: 'down' }) {
  return (
    <div className={`rounded-md bg-bg-3 p-3 text-sm ${tone === 'down' ? 'text-down' : 'text-ink-3'}`}>
      {children}
    </div>
  );
}

function KV({ k, children }: { k: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-ink-3">{k}</span>
      <span className="flex min-w-0 items-center text-ink-1">{children}</span>
    </div>
  );
}

/** 金额文本,按正负染色;空值显示灰 —。emphasized=结论列加重。 */
function MoneyText({ m, emphasized }: { m: Money | null | undefined; emphasized?: boolean }) {
  if (!m || m.value == null) return <span className="text-ink-3">—</span>;
  const n = moneyToNumber(m);
  const tone = n > 0 ? 'text-up' : n < 0 ? 'text-down' : 'text-ink-1';
  return <span className={`num ${tone} ${emphasized ? 'font-medium' : ''}`}>{fmtMoney(m, { sign: true })}</span>;
}

/** 等宽 ID + 一键复制(对账贴 cTrader 后台)。 */
function CopyText({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard?.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <span className="flex items-center gap-1">
      <span className="num text-ink-1">{value}</span>
      <button
        type="button"
        onClick={copy}
        className="text-ink-3 hover:text-ink-1"
        title="复制"
        aria-label="复制"
      >
        {copied ? <Check className="h-3.5 w-3.5 text-up" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : '加载失败';
}
